import React from 'react';

import { useLocation } from 'react-router-dom';
import { NavLink } from '~/lib/react-router-dom';

import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import ContactPhoneOutlinedIcon from '@mui/icons-material/ContactPhoneOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import PlaylistAddOutlinedIcon from '@mui/icons-material/PlaylistAddOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import { Badge, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';

import { nativeTourPath } from '../nativeTourModel';

import { NATIVE_TOUR_SECTIONS, NativeTourSection, NativeTourSectionState } from './nativeTourForm';

interface Props {
  legacyTourId: string;
  sectionStates: Record<NativeTourSection, NativeTourSectionState>;
}

const ICONS: Record<NativeTourSection, React.ReactElement> = {
  overview: <DescriptionOutlinedIcon />,
  'tour-options': <ExploreOutlinedIcon />,
  pricing: <SellOutlinedIcon />,
  'dynamic-pricing': <LocalOfferOutlinedIcon />,
  booking: <PaymentsOutlinedIcon />,
  'optional-experiences': <PlaylistAddOutlinedIcon />,
  media: <PhotoLibraryOutlinedIcon />,
  'on-sale': <CampaignOutlinedIcon />,
  'fine-print': <GavelOutlinedIcon />,
  reviews: <RateReviewOutlinedIcon />,
  operations: <ContactPhoneOutlinedIcon />,
};

export default function NativeTourNav({ legacyTourId, sectionStates }: Props) {
  const { pathname } = useLocation();
  const base = nativeTourPath(legacyTourId);
  const hrefOf = (path: string) => (path ? `${base}/${path}` : base);
  const current =
    NATIVE_TOUR_SECTIONS.find((section) => section.path && pathname.startsWith(hrefOf(section.path))) ??
    NATIVE_TOUR_SECTIONS[0];

  return (
    <List component="nav" aria-label="Tour sections" disablePadding>
      {NATIVE_TOUR_SECTIONS.map((section) => {
        const state = sectionStates[section.key];
        return (
          <ListItemButton
            key={section.key}
            component={NavLink}
            to={hrefOf(section.path)}
            selected={section.key === current.key}
            sx={{ alignItems: 'flex-start', py: 1.25 }}
          >
            <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
              <Badge
                color={state.isInvalid ? 'error' : 'primary'}
                variant="dot"
                invisible={!state.isInvalid && !state.isChanged}
              >
                {ICONS[section.key]}
              </Badge>
            </ListItemIcon>
            <ListItemText
              primary={section.label}
              secondary={state.isInvalid ? 'Needs fixing' : state.isChanged ? 'Unsaved changes' : section.description}
              secondaryTypographyProps={{
                variant: 'caption',
                color: state.isInvalid ? 'error' : state.isChanged ? 'primary' : 'text.secondary',
              }}
            />
          </ListItemButton>
        );
      })}
    </List>
  );
}
